import { sendTypedMessage } from './iframeUtil'

const TOKEN_REFRESH_TIMEOUT = 5000

let localJWTtoken = null
let tokenExpiryTime = null

// JWT token refresh queue management
let isRefreshingToken = false
let failedQueue = []

// Token waiting mechanism
let tokenPromise = null
let tokenResolve = null
let tokenReject = null

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })

  failedQueue = []
}

function parseJWTToken(token) {
  return JSON.parse(atob(token.split('.')[1]))
}

export function setJWTToken(token) {
  localJWTtoken = token

  try {
    const payload = parseJWTToken(token)
    tokenExpiryTime = payload.exp * 1000

    // Resolve any waiting promises
    if (tokenResolve) {
      tokenResolve(token)
      tokenResolve = null
      tokenReject = null
      tokenPromise = null
    }
  } catch (e) {
    console.error('Error parsing JWT token:', e)
    // Reject any waiting promises on error
    if (tokenReject) {
      tokenReject(e)
      tokenResolve = null
      tokenReject = null
      tokenPromise = null
    }
  }
}

export function checkForJWTToken() {
  const hasToken = localJWTtoken !== null
  const isExpired = tokenExpiryTime && Date.now() >= tokenExpiryTime
  return hasToken && !isExpired
}

export function getJWTToken() {
  return localJWTtoken
}

export function waitForJWTToken(timeout = TOKEN_REFRESH_TIMEOUT) {
  // If we already have a valid token, return it immediately
  if (checkForJWTToken()) {
    return Promise.resolve(localJWTtoken)
  }

  // If we're already waiting for a token, return the existing promise
  if (tokenPromise) {
    return tokenPromise
  }

  // Create a new promise to wait for the token
  tokenPromise = new Promise((resolve, reject) => {
    tokenResolve = resolve
    tokenReject = reject

    // Set a timeout to reject the promise if no token is received
    setTimeout(() => {
      if (tokenReject) {
        tokenReject(new Error('Token wait timeout'))
        tokenResolve = null
        tokenReject = null
        tokenPromise = null
      }
    }, timeout)
  })

  return tokenPromise
}

export async function requestJWTTokenWithTimeout() {
  return Promise.race([
    sendTypedMessage('RequestJWTToken', ''),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Token refresh timeout')), TOKEN_REFRESH_TIMEOUT)
    )
  ])
}

export function getIsRefreshingToken() {
  return isRefreshingToken
}

export function setIsRefreshingToken(value) {
  isRefreshingToken = value
}

export function addToFailedJWTTokenQueue(resolve, reject) {
  failedQueue.push({ resolve, reject })
}

export function processJWTTokenQueue(error, token = null) {
  processQueue(error, token)
}
